import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const ExternalIdsTab = withListOverflow(ExternalIdsListOwner);
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  confirmExternalLocationMapping,
  createExternalLocationMapping,
  deleteExternalLocationMapping,
  getExternalLocationMappings,
  updateExternalLocationMapping,
} from "../../data/adminClient.js";

const SOURCES = ["all", "envoy", "workday", "spacebase"];
const SOURCE_LABELS = { all: "All sources", envoy: "Envoy", workday: "Workday", spacebase: "Spacebase" };

const STATUS_META = {
  confirmed: { label: "Confirmed", fg: T.successText, bg: T.greenSoft, dot: T.green },
  needs_confirm: { label: "Needs confirm", fg: T.ragAmberText, bg: T.ragAmberBg, dot: T.amber },
  ignored: { label: "Ignored", fg: T.mid, bg: T.lightGray, dot: T.mid },
};

function statusChip(row) {
  if (row.isOverride) return { label: "Override", fg: T.blue, bg: T.blueSoft, dot: T.blue };
  return STATUS_META[row.status] || STATUS_META.needs_confirm;
}

function sourceLabel(source) {
  return source ? source[0].toUpperCase() + source.slice(1) : "—";
}

function formatRange(row) {
  if (!row.effectiveFrom && !row.effectiveTo) return "identity / open";
  return `${row.effectiveFrom || "open"} → ${row.effectiveTo || "present"}`;
}

function StatusChip({ row }) {
  const meta = statusChip(row);
  return (
    <span
      data-testid={`mapping-status-${row.id}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 999,
        padding: "3px 8px", fontSize: T.fsXxs5, fontWeight: T.fwBold,
        color: meta.fg, background: meta.bg, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: meta.dot }} />
      {meta.label}
    </span>
  );
}

function ExceptionCard({ title, rows, tone, actionLabel, onAction }) {
  return (
    <div
      data-testid={`external-ids-exception-${tone}`}
      style={{
        flex: 1, minWidth: 280, border: `1px solid ${T.border}`, borderLeft: `3px solid ${tone === "info" ? T.blue : T.amber}`,
        borderRadius: 9, padding: "12px 13px", background: tone === "info" ? T.blueXLight : T.card,
      }}
    >
      <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
        {title} ({rows.length})
      </div>
      {rows.length === 0 ? (
        <div style={{ color: T.light, fontSize: T.fsSm }}>None requiring review.</div>
      ) : rows.slice(0, 3).map((row) => (
        <div key={row.id} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", margin: "6px 0", fontSize: T.fsSm }}>
          <span>
            <b>{sourceLabel(row.source)}</b> · {row.sourceName || row.sourceLocationId}
            <span style={{ display: "block", color: T.light, fontSize: T.fsXs }}>{row.sourceLocationId}</span>
          </span>
          <button
            type="button"
            onClick={() => onAction(row)}
            style={{ border: `1px solid ${T.blue}`, color: T.blue, background: T.blueSoft, borderRadius: 6, padding: "3px 9px", fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer" }}
          >
            {actionLabel}
          </button>
        </div>
      ))}
    </div>
  );
}

function emptyForm() {
  return {
    source: "envoy", sourceLocationId: "", sourceName: "", officeId: "",
    effectiveFrom: "", effectiveTo: "", status: "needs_confirm", isOverride: false, notes: "",
  };
}

function ExternalIdsListOwner({ initialSource = "all" }) {
  const [source, setSource] = useState(initialSource);
  const [data, setData] = useState({ mappings: [], unmapped: [], exceptions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getExternalLocationMappings(source));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data.mappings || []).filter((row) => source === "all" || row.source === source), [data.mappings, source]);
  const unmapped = useMemo(() => (data.unmapped || []).filter((row) => source === "all" || row.source === source), [data.unmapped, source]);
  const disagreements = useMemo(() => (data.exceptions || []).filter((row) => row.isOverride && (source === "all" || row.source === source)), [data.exceptions, source]);
  const saoRows = rows.filter((row) => row.source === "workday" && row.sourceLocationId === "Sao Paulo Office");

  function openNew() {
    setNotice(null);
    setForm(emptyForm());
  }

  function openEdit(row) {
    setNotice(null);
    setForm({
      source: row.source, sourceLocationId: row.sourceLocationId, sourceName: row.sourceName || "",
      officeId: row.officeId || "", effectiveFrom: row.effectiveFrom || "", effectiveTo: row.effectiveTo || "",
      status: row.status, isOverride: !!row.isOverride, notes: row.notes || "", id: row.id,
    });
  }

  function openMap(row) {
    setNotice(null);
    setForm({
      ...emptyForm(),
      source: row.source,
      sourceLocationId: row.sourceLocationId,
      sourceName: row.sourceName || "",
    });
  }

  function change(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const payload = {
        source: form.source,
        sourceLocationId: form.sourceLocationId,
        sourceName: form.sourceName || null,
        officeId: form.officeId || null,
        effectiveFrom: form.effectiveFrom || null,
        effectiveTo: form.effectiveTo || null,
        status: form.status,
        isOverride: form.isOverride,
        notes: form.notes || null,
      };
      if (form.id) await updateExternalLocationMapping(form.id, payload);
      else await createExternalLocationMapping(payload);
      setForm(null);
      setNotice("Mapping saved.");
      await load();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function confirm(row) {
    try {
      await confirmExternalLocationMapping(row.id);
      setNotice("Mapping confirmed.");
      await load();
    } catch (err) { setNotice(err.message); }
  }

  async function remove(row) {
    if (!window.confirm(`Delete the ${sourceLabel(row.source)} mapping for ${row.sourceLocationId}?`)) return;
    try {
      await deleteExternalLocationMapping(row.id);
      setNotice("Mapping deleted.");
      await load();
    } catch (err) { setNotice(err.message); }
  }

  return (
    <div data-testid="external-ids-tab">
      <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: T.fsLg5, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>External Location IDs</div>
          <div style={{ fontSize: T.fsSm, color: T.mid, lineHeight: 1.5, maxWidth: 850 }}>
            Map each external system location to our canonical office ID. Effective-dated mappings preserve point-in-time attribution when an office moves.
          </div>
        </div>
        <button type="button" data-testid="external-ids-add" onClick={openNew} style={{ background: T.blue, color: T.white, border: 0, borderRadius: 7, padding: "8px 13px", fontWeight: T.fwBold, cursor: "pointer", fontFamily: "inherit" }}>
          Add mapping
        </button>
      </div>

      {notice && <div role="status" style={{ marginTop: 12, padding: "8px 10px", borderRadius: 7, background: T.blueSoft, color: T.dark, fontSize: T.fsSm }}>{notice}</div>}
      {error && <ErrorBanner error={error} />}

      <div style={{ display: "flex", gap: 12, margin: "16px 0", flexWrap: "wrap" }}>
        <ExceptionCard title="Unmapped source locations" rows={unmapped} tone="warning" actionLabel="Map" onAction={openMap} />
        <ExceptionCard title="Source disagrees / override" rows={disagreements} tone="info" actionLabel="Review" onAction={openEdit} />
      </div>

      <div style={{ display: "flex", gap: 7, marginBottom: 10 }}>
        {SOURCES.map((key) => (
          <button
            key={key}
            type="button"
            data-testid={`external-ids-filter-${key}`}
            aria-pressed={source === key}
            onClick={() => setSource(key)}
            style={{
              fontSize: T.fsXs, fontWeight: T.fwBold, border: `1px solid ${source === key ? T.dark : T.border}`,
              borderRadius: 999, padding: "4px 11px", color: source === key ? T.white : T.mid,
              background: source === key ? T.dark : T.card, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            {SOURCE_LABELS[key]}
          </button>
        ))}
      </div>

      {loading ? <div style={{ color: T.mid, fontSize: T.fsSm }}>Loading external mappings…</div> : (
        rows.length === 0 ? <div style={{ color: T.light, padding: "24px 0", fontSize: T.fsSm }}>No external mappings match this source.</div> : (
          <div style={{ overflowX: "auto", border: `1px solid ${T.border}`, borderRadius: 9 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsSm }}>
              <thead><tr style={{ background: T.tableHeadBg }}>
                {["Source", "Source location", "Maps to office", "Effective", "Status", "Actions"].map((heading) => (
                  <th key={heading} style={{ textAlign: "left", padding: "8px 10px", color: T.light, fontSize: T.fsXxxs5, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: 0.4 }}>{heading}</th>
                ))}
              </tr></thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} data-testid={`external-ids-row-${row.id}`} style={{ borderTop: `1px solid ${T.border}` }}>
                    <td style={{ padding: "10px", fontWeight: T.fwBold, color: row.source === "envoy" ? T.red : row.source === "workday" ? T.blue : T.greenDeep }}>{sourceLabel(row.source)}</td>
                    <td style={{ padding: "10px" }}><b>{row.sourceName || "—"}</b><div style={{ color: T.mid, fontFamily: "monospace", fontSize: T.fsXs }}>{row.sourceLocationId}</div></td>
                    <td style={{ padding: "10px" }}>{row.officeId ? <><b>{row.officeId}</b>{row.officeAlias ? <span style={{ color: T.mid }}> · {row.officeAlias}</span> : null}</> : <span style={{ color: T.light }}>not mapped</span>}</td>
                    <td style={{ padding: "10px", color: T.mid, fontFamily: "monospace", fontSize: T.fsXs }}>{formatRange(row)}</td>
                    <td style={{ padding: "10px" }}><StatusChip row={row} /></td>
                    <td style={{ padding: "10px", whiteSpace: "nowrap" }}>
                      <button type="button" onClick={() => openEdit(row)} style={{ border: 0, background: "transparent", color: T.blue, cursor: "pointer", fontWeight: T.fwBold }}>Edit</button>
                      {row.status === "needs_confirm" && <button type="button" onClick={() => confirm(row)} style={{ border: 0, background: "transparent", color: T.greenDeep, cursor: "pointer", fontWeight: T.fwBold }}>Confirm</button>}
                      <button type="button" onClick={() => remove(row)} style={{ border: 0, background: "transparent", color: T.red, cursor: "pointer", fontWeight: T.fwBold }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {saoRows.length > 0 && (
        <div data-testid="external-ids-sao-timeline" style={{ marginTop: 16, border: `1px solid ${T.border}`, borderRadius: 9, padding: "14px 15px", background: T.card }}>
          <div style={{ fontSize: T.fsBase5, fontWeight: T.fwBold, color: T.dark }}>How an office move is handled — São Paulo, moved Sep 1</div>
          <div style={{ fontSize: T.fsSm, color: T.mid, marginTop: 3, marginBottom: 12 }}>The mapping is effective-dated, so historical months remain attributed to the office occupied at that time.</div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ flex: 1, height: 8, borderRadius: 5, background: T.barTintBorder }} />
            <div style={{ width: 2, height: 30, background: T.red }} />
            <div style={{ flex: 1, height: 8, borderRadius: 5, background: T.blue }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: T.fsXs, fontWeight: T.fwBold, marginTop: 6 }}>
            <span>AMER-SAO-03 · Jan–Aug</span><span style={{ color: T.red }}>Sep 1 move</span><span>AMER-SAO-05 · Sep+</span>
          </div>
        </div>
      )}

      {form && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.22)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 20 }}>
          <form onSubmit={save} data-testid="external-ids-form" style={{ width: "min(560px, calc(100vw - 32px))", background: T.card, borderRadius: 10, padding: 20, boxShadow: "0 10px 30px rgba(15,23,42,0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <b style={{ fontSize: T.fsMd5, color: T.dark }}>{form.id ? "Edit mapping" : "Add external mapping"}</b>
              <button type="button" onClick={() => setForm(null)} style={{ border: 0, background: "transparent", color: T.mid, cursor: "pointer", fontSize: T.fsLg }}>×</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                ["source", "Source", "select", SOURCES.slice(1)],
                ["status", "Status", "select", ["confirmed", "needs_confirm", "ignored"]],
                ["sourceLocationId", "Source location ID", "text"],
                ["sourceName", "Source location name", "text"],
                ["officeId", "Canonical office ID", "text"],
                ["effectiveFrom", "Effective from", "date"],
                ["effectiveTo", "Effective to", "date"],
                ["notes", "Notes", "text"],
              ].map(([field, label, type, options]) => (
                <label key={field} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: T.fsXs, color: T.mid }}>
                  {label}
                  {type === "select" ? (
                    <select value={form[field]} onChange={(e) => change(field, e.target.value)} style={{ padding: "7px 8px", border: `1px solid ${T.borderDark}`, borderRadius: 6, fontFamily: "inherit" }}>
                      {options.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  ) : <input type={type} value={form[field]} onChange={(e) => change(field, e.target.value)} required={field === "sourceLocationId"} style={{ padding: "7px 8px", border: `1px solid ${T.borderDark}`, borderRadius: 6, fontFamily: "inherit" }} />}
                </label>
              ))}
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 12, fontSize: T.fsSm, color: T.mid }}>
              <input type="checkbox" checked={form.isOverride} onChange={(e) => change("isOverride", e.target.checked)} />
              Mark as override
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button type="button" onClick={() => setForm(null)} style={{ border: `1px solid ${T.border}`, background: T.card, color: T.mid, borderRadius: 6, padding: "7px 12px", cursor: "pointer" }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ border: 0, background: T.blue, color: T.white, borderRadius: 6, padding: "7px 12px", fontWeight: T.fwBold, cursor: "pointer" }}>{saving ? "Saving…" : "Save mapping"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}